import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [practiceAnchor, setPracticeAnchor] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, streak, loading } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { hasActiveSubscription } = useSubscriptionStatus();

  const primaryItems = [
    { text: 'Courses', path: '/courses' },
  ];

  const practiceItems = [
    { text: 'TopSWE100', path: '/problems' },
    { text: 'Quizzes', path: '/quizzes' },
    { text: 'Flashcards', path: '/flashcards' },
    { text: 'Roadmap', path: '/roadmap' },
  ];

  const isActive = (path) => location.pathname === path;
  const isPracticeActive = practiceItems.some((item) => isActive(item.path));
  const closeDrawer = () => setMobileOpen(false);
  const closePracticeMenu = () => setPracticeAnchor(null);

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        bgcolor: isDarkMode ? 'rgba(9, 12, 18, 0.78)' : 'rgba(255, 255, 255, 0.82)',
        backdropFilter: 'blur(18px)',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: 68, justifyContent: 'space-between', gap: 2 }}>
          <Button
            component={Link}
            to="/"
            sx={{
              color: 'text.primary',
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '1rem',
              p: 0,
              minWidth: 0,
              gap: 1,
              '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
            }}
          >
            <Box component="img" src="/topswe-logo.png" alt="TopSWE" sx={{ width: 30, height: 30, objectFit: 'contain' }} />
            TopSWE
          </Button>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {primaryItems.map((item) => (
              <Button
                key={item.text}
                component={Link}
                to={item.path}
                sx={{
                  color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                  px: 1.4,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    color: 'text.primary',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {item.text}
              </Button>
            ))}
            <Button
              onClick={(event) => setPracticeAnchor(event.currentTarget)}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                color: isPracticeActive ? 'primary.main' : 'text.secondary',
                px: 1.4,
                py: 1,
                borderRadius: 2,
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                },
              }}
            >
              Practice
            </Button>
            <Menu
              anchorEl={practiceAnchor}
              open={Boolean(practiceAnchor)}
              onClose={closePracticeMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 180,
                  p: 0.6,
                },
              }}
            >
              {practiceItems.map((item) => (
                <MenuItem
                  key={item.text}
                  component={Link}
                  to={item.path}
                  onClick={closePracticeMenu}
                  selected={isActive(item.path)}
                  sx={{ borderRadius: 1.5, fontWeight: 650 }}
                >
                  {item.text}
                </MenuItem>
              ))}
            </Menu>
            {!(user && hasActiveSubscription) && (
              <Button
                component={Link}
                to="/purchase"
                variant="contained"
                sx={{
                  px: 2.2,
                  py: 1,
                  borderRadius: 2,
                }}
              >
                Pricing
              </Button>
            )}
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            {!loading && user && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: 'text.secondary', px: 1 }}>
                <WhatshotIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {streak}
                </Typography>
              </Box>
            )}
            {!loading && user && (
              <IconButton component={Link} to="/profile" aria-label="Profile" sx={{ color: 'text.secondary' }}>
                <AccountCircleIcon />
              </IconButton>
            )}
            <Tooltip title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton onClick={toggleTheme} aria-label="Toggle theme" sx={{ color: 'text.secondary' }}>
                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            {!loading && !user && (
              <Button component={Link} to="/signin" variant="outlined" sx={{ px: 2.2 }}>
                Sign in
              </Button>
            )}
          </Box>

          <IconButton
            color="inherit"
            aria-label="Open navigation"
            edge="end"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </Container>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={closeDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            bgcolor: 'background.default',
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontWeight: 800, mb: 1 }}>TopSWE</Typography>
          <List disablePadding>
            {primaryItems.map((item) => (
              <ListItemButton
                key={item.text}
                component={Link}
                to={item.path}
                onClick={closeDrawer}
                selected={isActive(item.path)}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
            <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, mt: 1.5, mb: 0.5, px: 1.5 }}>
              Practice
            </Typography>
            {practiceItems.map((item) => (
              <ListItemButton
                key={item.text}
                component={Link}
                to={item.path}
                onClick={closeDrawer}
                selected={isActive(item.path)}
                sx={{ borderRadius: 2, mb: 0.5, pl: 3 }}
              >
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
            {!(user && hasActiveSubscription) && (
              <ListItemButton
                component={Link}
                to="/purchase"
                onClick={closeDrawer}
                selected={isActive('/purchase')}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemText primary="Pricing" />
              </ListItemButton>
            )}
            {!loading && user && (
              <ListItemButton component={Link} to="/profile" onClick={closeDrawer} sx={{ borderRadius: 2, mb: 0.5 }}>
                <ListItemText primary="Profile" secondary={`${streak} day streak`} />
              </ListItemButton>
            )}
            <ListItemButton onClick={toggleTheme} sx={{ borderRadius: 2, mb: 0.5 }}>
              <ListItemText primary={isDarkMode ? 'Light mode' : 'Dark mode'} />
            </ListItemButton>
            {!loading && !user && (
              <ListItemButton
                onClick={() => {
                  closeDrawer();
                  navigate('/signin');
                }}
                sx={{ borderRadius: 2, mt: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}
              >
                <ListItemText primary="Sign in" />
              </ListItemButton>
            )}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}

export default Navbar;
